import { Stack, Typography, useTheme } from "@mui/material";
import { Banknote, Home } from "lucide-react";
import type { FC } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InspirationExampleText } from "web_myplann/Inspiration/Components/InspirationExampleText";
import { InspirationSection } from "web_myplann/Inspiration/Components/InspirationSection";
import { ComparisonColumn } from "web_myplann/Inspiration/OwnHome/Components/ComparisonColumn";
import { OWN_HOME_HORIZON_YEARS } from "web_myplann/Inspiration/OwnHome/constants";
import type { OwnHomeContext } from "web_myplann/Inspiration/OwnHome/Types/OwnHomeTypes";
import { formatMillionRange } from "web_myplann/Inspiration/OwnHome/Utils/formatMillionRange";
import { formatRateBand } from "web_myplann/Inspiration/OwnHome/Utils/formatRateBand";
import { formatInspirationMoney } from "web_myplann/Inspiration/Utils/inspirationMoney";

const R = Resources.Inspiration.OwnHome.Result;

type Props = {
	context: OwnHomeContext;
};

export const RentVsOwnComparison: FC<Props> = ({ context }) => {
	const { t } = useResource();
	const theme = useTheme();

	const {
		rent,
		savings,
		totalRent,
		priceLow,
		priceHigh,
		propertyLow,
		propertyHigh,
		state,
		rates,
	} = context;

	return (
		<InspirationSection
			title={t(R.comparisonTitle)}
			gap={theme.spacingTokens.md}
		>
			<Stack gap={theme.spacingTokens.xs}>
				<InspirationExampleText
					text={savings > 0 ? R.comparisonTextWithSavings : R.comparisonText}
					values={{
						rent: formatInspirationMoney(rent),
						savings: formatInspirationMoney(savings),
						range: formatMillionRange(priceLow, priceHigh, t),
					}}
					relaxed
					color={theme.palette.grey[500]}
				/>

				<Typography variant="hint" color={theme.palette.grey[500]}>
					{t(R.comparisonSourceNote)}
				</Typography>
			</Stack>

			<Stack direction="row" gap={theme.spacingTokens.md}>
				<ComparisonColumn
					bg={theme.palette.surface.muted}
					iconBg={theme.palette.grey[200]}
					icon={
						<Banknote
							size={theme.iconSize.sm}
							color={theme.palette.grey[500]}
						/>
					}
					tag={t(R.comparisonRentTag)}
					tagColor={theme.palette.grey[500]}
					totalValue={formatInspirationMoney(totalRent)}
					dividerColor={theme.palette.grey[200]}
					endLabel={t(R.comparisonEndLabel)}
					endValue={
						<Typography variant="hint" strong color={theme.palette.red.main}>
							{t(R.comparisonRentEnd)}
						</Typography>
					}
				/>
				<ComparisonColumn
					bg={theme.palette.blue[50]}
					iconBg={theme.palette.blue[100]}
					icon={
						<Home size={theme.iconSize.sm} color={theme.palette.blue.main} />
					}
					tag={t(R.comparisonOwnTag)}
					tagColor={theme.palette.blue.main}
					totalValue={`~${formatInspirationMoney(totalRent)}`}
					dividerColor={theme.palette.blue[100]}
					endLabel={t(R.comparisonPropertyLabel, {
						years: OWN_HOME_HORIZON_YEARS,
					})}
					endValue={
						<Typography
							variant="hint"
							strong
							color={theme.palette.blue.main}
							tabular
						>
							{formatMillionRange(propertyLow, propertyHigh, t)}
						</Typography>
					}
				/>
			</Stack>

			<Typography variant="hint" color={theme.palette.grey[500]} relaxed>
				{t(R.comparisonHint, { rates: formatRateBand(rates) })}
				{state === "affordability" && t(R.comparisonHintExpenses)}
			</Typography>
		</InspirationSection>
	);
};
